import ipaddr from 'ipaddr.js';

import type {
  RedshiftConnectionConfig,
  RedshiftIamConnectionConfig,
  RedshiftPasswordConnectionConfig,
  RedshiftRsConfig,
  RedshiftSslMode,
} from '../types.js';
import { isUnsafeIpLiteral, normalizeIPv4Literal } from './private-ip.js';
import {
  isRsTruthyFlag,
  pickPortString,
  pickString,
  pickUnknown,
  RS_CLUSTER_ID_KEYS,
  RS_HOST_KEYS,
  RS_IAM_FLAG_KEYS,
  RS_NAMESPACE_KEYS,
  RS_PORT_KEYS,
  RS_REGION_KEYS,
  RS_ROLE_ARN_KEYS,
  RS_USER_KEYS,
} from './redshift-rs-config.js';
import { parseWarehouseConnection } from './warehouse-connection.js';
import { privateWarehouseHostsAllowed } from './warehouse-host-policy.js';
import type { WarehouseHostValidationOptions } from './warehouse-host-validation.js';
import { isValidDnsHostname } from './warehouse-host-validation.js';

export {
  isRsTruthyFlag,
  pickString,
  RS_CLUSTER_ID_KEYS,
  RS_HOST_KEYS,
  RS_IAM_FLAG_KEYS,
  RS_REGION_KEYS,
  RS_ROLE_ARN_KEYS,
} from './redshift-rs-config.js';

export const DEFAULT_REDSHIFT_PORT = 5439;
/** Pin verify-full so pg v9+ cannot weaken require; relies on system CA trust for Redshift endpoints. */
export const DEFAULT_REDSHIFT_SSLMODE = 'verify-full' as const satisfies RedshiftSslMode;

const RS_WAREHOUSE_TYPES = new Set(['RS', 'REDSHIFT']);

const SAFE_SCHEMA_IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

const PROBE_URL_HOST = '_';

// eslint-disable-next-line security/detect-unsafe-regex -- reviewed: linear, no nested/overlapping quantifiers (fixed {3} repeat of a bounded {1,3} digit group), input is a short host string
const IPV4_LITERAL = /^\d{1,3}(\.\d{1,3}){3}$/;

const REDSHIFT_JSON_AUTH_TYPES = new Set(['password', 'managed_password', 'iam']);

export function isRedshiftWarehouseType(type: string | undefined): boolean {
  return type !== undefined && RS_WAREHOUSE_TYPES.has(type.toUpperCase());
}

function isRedshiftJsonRecord(record: Record<string, unknown>): boolean {
  const authType = record['authType'];
  if (typeof authType !== 'string' || !REDSHIFT_JSON_AUTH_TYPES.has(authType)) return false;
  if (authType === 'iam') {
    return (
      typeof record['host'] === 'string' &&
      record['host'].trim().length > 0 &&
      typeof record['database'] === 'string' &&
      record['database'].trim().length > 0 &&
      typeof record['user'] === 'string' &&
      record['user'].trim().length > 0 &&
      typeof record['clusterIdentifier'] === 'string' &&
      record['clusterIdentifier'].trim().length > 0 &&
      typeof record['clusterRegion'] === 'string' &&
      record['clusterRegion'].trim().length > 0 &&
      typeof record['iamRoleArn'] === 'string' &&
      record['iamRoleArn'].trim().length > 0
    );
  }
  return (
    typeof record['host'] === 'string' ||
    typeof record['database'] === 'string' ||
    typeof record['user'] === 'string' ||
    'port' in record ||
    'schema' in record
  );
}

/** True when stored value is a JSON Redshift config blob (not a legacy postgres URL). */
export function isRedshiftJsonBlob(stored: string): boolean {
  const trimmed = stored.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
    return isRedshiftJsonRecord(parsed as Record<string, unknown>);
  } catch {
    return false;
  }
}

export function isRedshiftIamBlob(stored: string): boolean {
  const trimmed = stored.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
    const record = parsed as Record<string, unknown>;
    if (record['authType'] !== 'iam') return false;
    return isRedshiftJsonRecord(record);
  } catch {
    return false;
  }
}

/** True when stored JSON declares IAM auth but fails structured validation (fail-closed guard). */
export function isIncompleteRedshiftIamBlob(stored: string): boolean {
  const trimmed = stored.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(trimmed) as { authType?: string };
    return parsed.authType === 'iam' && !isRedshiftIamBlob(stored);
  } catch {
    return false;
  }
}

/**
 * Safe SQL identifier check (letters, digits, underscores; must start with a
 * letter or underscore) — the single source of truth for this rule, shared by the
 * Redshift namespace and the Databricks catalog/schema. Exposed so the connection
 * forms can validate these fields client-side against the exact backend regex
 * instead of drifting from it.
 */
export function isSafeSqlIdentifier(name: string): boolean {
  return SAFE_SCHEMA_IDENT.test(name);
}

export function isSafeRedshiftSchema(name: string): boolean {
  return isSafeSqlIdentifier(name);
}

/**
 * Canonical AWS region shape. The leading `[a-z]{2}` with an optional `-gov` already
 * covers commercial (`us-east-1`), GovCloud (`us-gov-west-1`), and China (`cn-north-1`)
 * region ids. The single source of truth, shared by the warehouse Zod schema and the
 * Redshift IAM form so client and server validation can't drift.
 */
const AWS_REGION_REGEX = /^[a-z]{2}(-gov)?-[a-z]+-\d$/;

/** True when the value looks like an AWS region id (tolerates surrounding whitespace). */
export function isValidAwsRegion(region: string): boolean {
  return AWS_REGION_REGEX.test(region.trim());
}

/**
 * Canonical IAM role ARN shape; capture group 1 is the embedded 12-digit AWS account ID.
 * The single source of truth, shared by the warehouse Zod schema (validity) and the
 * Redshift IAM form (validity + deriving the account ID for the policy JSON).
 */
const IAM_ROLE_ARN_REGEX = /^arn:aws[a-zA-Z-]*:iam::(\d{12}):role\/.+$/;

/** True when the value is a well-formed IAM role ARN (tolerates surrounding whitespace). */
export function isValidIamRoleArn(roleArn: string): boolean {
  return IAM_ROLE_ARN_REGEX.test(roleArn.trim());
}

/**
 * Canonical Redshift cluster identifier shape: starts with a letter, then letters,
 * digits, and hyphens, and cannot end with a hyphen (max 63 chars). The single source
 * of truth, shared by the warehouse Zod schema (validity), the Redshift IAM form
 * (validity), and the IAM policy-JSON generator (which embeds the cluster id into the
 * GetClusterCredentials resource ARN) — so client, server, and generator can't drift.
 */
// eslint-disable-next-line security/detect-unsafe-regex -- reviewed: linear, single bounded {0,61} quantifier with no nested/overlapping repetition, input is a short cluster identifier
const REDSHIFT_CLUSTER_ID_REGEX = /^[A-Za-z](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

/** True when the value is a well-formed Redshift cluster identifier (tolerates surrounding whitespace). */
export function isValidRedshiftClusterIdentifier(clusterIdentifier: string): boolean {
  return REDSHIFT_CLUSTER_ID_REGEX.test(clusterIdentifier.trim());
}

/**
 * Extract the 12-digit AWS account ID from an IAM role ARN, or '' when the value isn't a
 * well-formed role ARN. Lets the Redshift IAM form derive the account ID for the policy
 * JSON instead of asking the admin for it separately.
 */
export function awsAccountIdFromRoleArn(roleArn: string): string {
  const match = IAM_ROLE_ARN_REGEX.exec(roleArn.trim());
  return match?.[1] ?? '';
}

export function isValidRedshiftHost(host: string, options: WarehouseHostValidationOptions = {}): boolean {
  const h = host.trim();
  if (!h || h.length > 253) return false;
  const bareHost = h.startsWith('[') && h.endsWith(']') ? h.slice(1, -1) : h;
  const allowPrivate = options.allowPrivate ?? false;
  if (allowPrivate && (h.toLowerCase() === 'localhost' || ipaddr.isValid(bareHost))) return true;
  if (h.toLowerCase() === 'localhost') return false;
  if (/\s/.test(h)) return false;
  if (/[@:/?#\\]/.test(h)) return false;
  if (IPV4_LITERAL.test(h)) return false;
  // Public IPv6 literals are intentionally unsupported at save — use a DNS hostname instead.
  if (h.startsWith('[') && h.endsWith(']')) return false;
  if (!isValidDnsHostname(h)) return false;

  const probe = new URL(`postgresql://${PROBE_URL_HOST}/`);
  try {
    probe.hostname = h;
  } catch {
    return false;
  }
  const normalized = probe.hostname.toLowerCase();
  if (normalized !== h.toLowerCase() || normalized === PROBE_URL_HOST) return false;
  const ipv4 = normalizeIPv4Literal(h);
  if (ipv4 !== null && ipv4 !== h) return false;
  if (isUnsafeIpLiteral(h)) return false;
  return true;
}

/** Bracket IPv6 for libpq URLs — only reachable when dev `allowPrivate` accepts an IP literal host. */
function redshiftUrlHostname(host: string): string {
  const trimmed = host.trim();
  const bareHost = trimmed.startsWith('[') && trimmed.endsWith(']') ? trimmed.slice(1, -1) : trimmed;
  if (ipaddr.isValid(bareHost) && ipaddr.parse(bareHost).kind() === 'ipv6') {
    return `[${bareHost}]`;
  }
  return trimmed;
}

/** AWS Redshift endpoint suffixes allowed for IAM connections (extend here for new AWS patterns). */
export const REDSHIFT_IAM_HOST_SUFFIXES = [
  '.redshift.amazonaws.com',
  '.redshift-serverless.amazonaws.com',
  '.redshift.amazonaws.com.cn',
  '.redshift-serverless.amazonaws.com.cn',
] as const;

export const REDSHIFT_IAM_HOST_VALIDATION_MESSAGE =
  'Host must be an standard AWS Redshift endpoint (e.g. *.redshift.amazonaws.com).';

export function isAllowedRedshiftIamHost(host: string, options: WarehouseHostValidationOptions = {}): boolean {
  const h = host.trim().toLowerCase();
  return isValidRedshiftHost(h, options) && REDSHIFT_IAM_HOST_SUFFIXES.some((suffix) => h.endsWith(suffix));
}

/**
 * The AWS identifier embedded in a standard Redshift endpoint — the leading label of
 * `<cluster-id>.<account>.<region>.redshift.amazonaws.com` (provisioned) or
 * `<workgroup>.<account>.<region>.redshift-serverless.amazonaws.com` (serverless). Returns ''
 * when the host is not a standard AWS Redshift endpoint (a custom domain, a private dev host).
 *
 * Exists because a Redshift *bucket* is not always keyed on a dialable endpoint: `deriveAccountKey`
 * (warehouse-keys.ts) takes `host || clusterId || workgroupName`, so a destination synced without a
 * host keys its bucket on the provisioned cluster id or the serverless workgroup name while the
 * connection config carries the endpoint that identifier resolves to. This is what lets the save
 * path check such a config against a host dim that is an identity rather than an endpoint.
 */
export function redshiftEndpointIdentifier(host: string): string {
  const h = host.trim().toLowerCase();
  if (!REDSHIFT_IAM_HOST_SUFFIXES.some((suffix) => h.endsWith(suffix))) return '';
  return h.split('.')[0] ?? '';
}

export function assertValidRedshiftHost(host: string, options: WarehouseHostValidationOptions = {}): string {
  const h = host.trim();
  if (!isValidRedshiftHost(h, options)) {
    throw new Error('Invalid Redshift host');
  }
  return h;
}

export function assertValidRedshiftSchema(schema: string | undefined): void {
  const s = schema?.trim();
  if (s && !isSafeRedshiftSchema(s)) {
    throw new Error('Invalid namespace: use letters, numbers, and underscores; must start with a letter or underscore');
  }
}

/** Schema for URL query param when resolving stored config (lenient for legacy invalid values). */
export function schemaForResolvedUrl(schema: string | undefined): string | undefined {
  const s = schema?.trim();
  if (!s || !isSafeRedshiftSchema(s)) return undefined;
  return s;
}

export type BuildRedshiftPostgresUrlOptions = {
  strict?: boolean;
  /** Override user (e.g. IAM-prefixed DbUser from GetClusterCredentials). */
  user?: string;
  /** Override password (e.g. temporary IAM database password). */
  password?: string;
};

export function buildRedshiftPostgresUrl(
  config: RedshiftConnectionConfig,
  options: BuildRedshiftPostgresUrlOptions = {},
): string {
  const strict = options.strict ?? true;
  const port = config.port || DEFAULT_REDSHIFT_PORT;
  const hostOptions = { allowPrivate: privateWarehouseHostsAllowed() };
  const hostname = redshiftUrlHostname(assertValidRedshiftHost(config.host, hostOptions));

  const schemaParam = strict
    ? (() => {
        assertValidRedshiftSchema(config.schema);
        return config.schema?.trim() || undefined;
      })()
    : schemaForResolvedUrl(config.schema);

  const sslmode = config.sslmode ?? DEFAULT_REDSHIFT_SSLMODE;
  const url = new URL(`postgresql://${PROBE_URL_HOST}/`);
  url.username = options.user ?? config.user;
  const password =
    options.password ??
    (config.authType === 'password' || config.authType === 'managed_password' ? (config.password ?? '') : '');
  url.password = password;
  url.hostname = hostname;
  url.port = String(port);
  url.pathname = `/${encodeURIComponent(config.database)}`;
  if (schemaParam) {
    url.searchParams.set('search_path', schemaParam);
  }
  url.searchParams.set('sslmode', sslmode);
  return url.toString();
}

const REDSHIFT_SSLMODES = new Set<RedshiftSslMode>(['disable', 'require', 'verify-full']);

/** The mode the Redshift dial path uses for `raw`: a known mode in lower case, else the default. */
export function clampRedshiftSslMode(raw: string | null | undefined): RedshiftSslMode {
  const normalized = raw?.toLowerCase();
  if (normalized && REDSHIFT_SSLMODES.has(normalized as RedshiftSslMode)) {
    return normalized as RedshiftSslMode;
  }
  return DEFAULT_REDSHIFT_SSLMODE;
}

export function clampRedshiftUrlSslMode(stored: string): string {
  try {
    const url = new URL(stored);
    const sslmode = url.searchParams.get('sslmode');
    // Legacy URLs without a mode must keep their existing no-TLS behavior.
    if (!sslmode?.trim()) return stored;
    url.searchParams.set('sslmode', clampRedshiftSslMode(sslmode));
    return url.toString();
  } catch {
    // An unreadable stored row must reach the caller's existing validation path.
    return stored;
  }
}

function parseRedshiftJsonBlob(
  raw: Partial<RedshiftConnectionConfig> & Record<string, unknown>,
): RedshiftConnectionConfig {
  const common = {
    host: typeof raw.host === 'string' ? raw.host : '',
    port: typeof raw.port === 'number' ? raw.port : Number(raw.port) || DEFAULT_REDSHIFT_PORT,
    database: typeof raw.database === 'string' ? raw.database : '',
    user: typeof raw.user === 'string' ? raw.user : '',
    schema: typeof raw.schema === 'string' ? raw.schema : undefined,
    sslmode: clampRedshiftSslMode(typeof raw.sslmode === 'string' ? raw.sslmode : undefined),
  };

  if (raw.authType === 'iam') {
    return {
      authType: 'iam',
      ...common,
      clusterIdentifier: typeof raw.clusterIdentifier === 'string' ? raw.clusterIdentifier : '',
      clusterRegion: typeof raw.clusterRegion === 'string' ? raw.clusterRegion : '',
      iamRoleArn: typeof raw.iamRoleArn === 'string' ? raw.iamRoleArn : '',
    } satisfies RedshiftIamConnectionConfig;
  }

  return {
    authType: raw.authType === 'managed_password' ? 'managed_password' : 'password',
    ...common,
    password: typeof raw.password === 'string' ? raw.password : undefined,
  } satisfies RedshiftPasswordConnectionConfig;
}

/** Parse stored connection (JSON config or legacy postgres URL) into structured config. */
export function parseRedshiftStoredConnection(
  stored: string,
  name = 'redshift',
  type = 'RS',
): RedshiftConnectionConfig {
  if (isRedshiftJsonBlob(stored)) {
    const raw = JSON.parse(stored) as Partial<RedshiftConnectionConfig> & Record<string, unknown>;
    return parseRedshiftJsonBlob(raw);
  }

  const parsed = parseWarehouseConnection(stored, name, type);
  const url = new URL(stored.startsWith('postgres') ? stored : `postgresql://${stored}`);
  const rawMode = url.searchParams.get('sslmode');
  const sslmode = rawMode?.trim() ? clampRedshiftSslMode(rawMode) : 'disable';

  return {
    authType: 'password',
    host: parsed.host,
    port: parsed.port || DEFAULT_REDSHIFT_PORT,
    database: parsed.dbname,
    user: parsed.user,
    password: parsed.password,
    schema: parsed.schema !== 'public' ? parsed.schema : undefined,
    sslmode,
  };
}

/** Convert stored RS connection to a postgres URL for the driver. */
export function resolveRedshiftConnectionForDriver(stored: string, warehouseType?: string): string {
  if (!isRedshiftWarehouseType(warehouseType) && !isRedshiftJsonBlob(stored)) {
    return stored;
  }
  if (isRedshiftIamBlob(stored)) {
    throw new Error('IAM Redshift connections must be resolved asynchronously via resolveRedshiftIamConnectionString');
  }
  return buildRedshiftPostgresUrl(parseRedshiftStoredConnection(stored, 'redshift', warehouseType ?? 'RS'), {
    strict: false,
  });
}

/** Derive form prefill from sanitized RudderStack destination config. */
export function redshiftPrefillFromRsConfig(rsConfig: RedshiftRsConfig): RedshiftConnectionConfig {
  const rsRecord = rsConfig as Record<string, unknown>;
  const host = pickString(rsRecord, RS_HOST_KEYS) ?? '';
  const portRaw = pickPortString(rsRecord, RS_PORT_KEYS) ?? '';
  const database = pickString(rsRecord, ['database']) ?? '';
  const user = pickString(rsRecord, RS_USER_KEYS) ?? '';
  const namespace = pickString(rsRecord, RS_NAMESPACE_KEYS) ?? '';
  const clusterIdentifier = pickString(rsRecord, RS_CLUSTER_ID_KEYS) ?? '';
  const clusterRegion = pickString(rsRecord, RS_REGION_KEYS) ?? '';
  const iamRoleArn = pickString(rsRecord, RS_ROLE_ARN_KEYS) ?? '';

  const port = portRaw ? Number(portRaw) : DEFAULT_REDSHIFT_PORT;
  const common = {
    host,
    port: Number.isFinite(port) ? port : DEFAULT_REDSHIFT_PORT,
    database,
    user,
    schema: namespace.trim() || undefined,
    sslmode: DEFAULT_REDSHIFT_SSLMODE,
  };

  if (isRedshiftRsConfigIam(rsConfig)) {
    return {
      authType: 'iam',
      ...common,
      clusterIdentifier,
      clusterRegion,
      iamRoleArn,
    };
  }

  return {
    authType: 'managed_password',
    ...common,
  };
}

/** True when RS destination uses IAM for database authentication (`useIAMForAuth`). */
export function isRedshiftRsConfigIam(rsConfig: RedshiftRsConfig | Record<string, string> | null | undefined): boolean {
  if (!rsConfig) return false;
  return isRsTruthyFlag(pickUnknown(rsConfig as Record<string, unknown>, RS_IAM_FLAG_KEYS));
}

/**
 * The PRIMARY schema from a postgres URL (after JSON resolution) — always a single
 * identifier, never a comma-separated list.
 *
 * Callers treat this as one schema name (`postgres.ts` binds it into
 * `information_schema.tables WHERE table_schema = $1`), so returning a list here silently
 * matched nothing. For the full list — which only `searchPathSetSql` needs — use
 * `searchPathListFromPostgresUrl`.
 */
export function searchPathFromPostgresUrl(connectionString: string): string {
  return searchPathListFromPostgresUrl(connectionString)[0] ?? 'public';
}

/**
 * The FULL validated search_path as a list — `['eval', 'noise']` for `?search_path=eval,noise`.
 *
 * Split from `searchPathFromPostgresUrl` because that function's callers want ONE schema name.
 * `postgres.ts` binds it straight into `information_schema.tables WHERE table_schema = $1`, so
 * returning the raw `'eval,noise'` matched no rows and reported a connection with **0 tables** —
 * a user-visible break for anyone with a multi-schema search_path. The single-schema accessor now
 * yields the PRIMARY schema, and only `searchPathSetSql` (which genuinely needs every segment)
 * consumes the list.
 *
 * Validation is unchanged and still all-or-nothing: every segment must independently pass
 * `isSafeRedshiftSchema`, and if any fails the WHOLE value is rejected to `['public']` rather
 * than partially honored. This does NOT loosen the single-identifier gate on a user-supplied
 * `schema` FORM FIELD — only how this URL param is parsed.
 */
export function searchPathListFromPostgresUrl(connectionString: string): string[] {
  try {
    const url = new URL(connectionString);
    const raw = url.searchParams.get('search_path')?.trim();
    if (raw) {
      const segments = raw.split(',').map((s) => s.trim());
      if (segments.every((s) => isSafeRedshiftSchema(s))) return segments;
    }
  } catch {
    // fall through
  }
  return ['public'];
}

/** Safe SET search_path SQL for a postgres/redshift connection URL. Supports a single
 * schema or a comma-separated list — each segment is validated (above) and quoted
 * individually, so a multi-schema search_path is exactly as safe as the single-schema
 * case. */
export function searchPathSetSql(connectionString: string): string {
  const schemas = searchPathListFromPostgresUrl(connectionString);
  return `SET search_path TO ${schemas.map(quoteSqlIdentifier).join(', ')}, public`;
}

/** Quote an SQL identifier with standard double-quote escaping. */
export function quoteSqlIdentifier(identifier: string): string {
  return `"${identifier.replace(/"/g, '""')}"`;
}

export type RedshiftMergeableConfig = Pick<RedshiftPasswordConnectionConfig, 'password'>;
